/**
 * Utilidades reutilizables para transformar errores de validación del
 * backend en mensajes por-campo que se pueden pintar inline en formularios.
 *
 * Formato esperado principal (envoltura ApiResponse de FastAPI):
 *   {
 *     success: false,
 *     error: 'validation_error',
 *     message: 'Los datos enviados no son válidos.',
 *     data: [{ loc: ['body','event_date'], msg: '...', type: '...' }, ...]
 *   }
 *
 * También soporta `{ detail: [...] }` y `{ errors: { field: 'msg' } }`.
 *
 * Entrada:
 *   err          : error de axios / rechazo del fetch wrapper.
 *   fieldLabels  : { fieldName: etiquetaHumana }     (opcional)
 *   overrides    : { fieldName: mensajeFijo }        (opcional, pisa heurística)
 *
 * Salida:
 *   {
 *     fieldErrors   : { fieldName: mensajeHumano },
 *     formMessage   : string | null,    // mejor mensaje para toast/banner
 *     hasFieldErrors: boolean
 *   }
 */

const SYSTEM_LOC_PREFIXES = new Set(['body', 'query', 'path', 'header', 'cookie']);

export const DEFAULT_FORM_ERROR_MESSAGE = 'Los datos enviados no son válidos.';

/**
 * Normaliza el `loc` de pydantic a un nombre de campo plano.
 * Ejemplos:
 *   ['body', 'event_date']     -> 'event_date'
 *   ['body', 'items', 0, 'name'] -> 'items.0.name'
 */
export function locToField(loc) {
  if (!Array.isArray(loc) || loc.length === 0) return null;
  const parts = loc.filter((p, i) => !(i === 0 && SYSTEM_LOC_PREFIXES.has(String(p))));
  return parts.map(String).join('.') || null;
}

/**
 * Construye un mensaje legible en español a partir de un error de pydantic.
 * No está acoplado a ningún formulario concreto: aplica reglas genéricas
 * basadas en el tipo del error y la etiqueta del campo. Si no reconoce el
 * tipo, devuelve `<label>: <mensaje original>` como fallback.
 */
export function translatePydanticError(field, entry, fieldLabels = {}) {
  const label = fieldLabels[field] || field;
  const type = (entry?.type || '').toLowerCase();
  const msg = (entry?.msg || '').toLowerCase();

  if (type.includes('datetime') || msg.includes('valid datetime')) {
    if (field.includes('date') || field.includes('fecha') || field.includes('hora')) {
      return `Falta completar la hora de ${label}.`;
    }
    return `${label} tiene un formato de fecha y hora no válido.`;
  }
  if (type.includes('date') || msg.includes('valid date')) {
    return `${label} tiene un formato de fecha no válido.`;
  }
  if (type.includes('time') || msg.includes('valid time')) {
    return `Falta completar la hora de ${label}.`;
  }

  if (type === 'missing' || msg.includes('field required')
      || msg.includes('value is required')) {
    return `Completa el campo ${label}.`;
  }

  if (type.includes('too_short') || msg.includes('too short')
      || msg.includes('at least')) {
    return `${label} es demasiado corto.`;
  }
  if (type.includes('too_long') || msg.includes('too long')
      || msg.includes('at most')) {
    return `${label} es demasiado largo.`;
  }

  if (type.includes('email') || msg.includes('valid email')) {
    return `${label} no es un correo válido.`;
  }
  if (type.includes('url') || msg.includes('valid url')) {
    return `${label} no es una URL válida.`;
  }

  if (type.includes('greater_than') || msg.includes('greater than')) {
    return `${label} debe ser mayor.`;
  }
  if (type.includes('less_than') || msg.includes('less than')) {
    return `${label} debe ser menor.`;
  }
  if (type.includes('number_parsing') || type.includes('int_parsing')
      || type.includes('float_parsing')) {
    return `${label} debe ser un número válido.`;
  }

  if (type.includes('enum') || type.includes('literal_error')) {
    return `${label} tiene un valor no permitido.`;
  }

  if (entry?.msg) return `${label}: ${entry.msg}`;
  return `${label} no es válido.`;
}

export function extractFieldErrors(err, options = {}) {
  const { fieldLabels = {}, overrides = {} } = options;
  const result = { fieldErrors: {}, formMessage: null, hasFieldErrors: false };

  const body = err?.response?.data ?? err?.payload ?? null;
  const rawDetail = body?.data ?? body?.detail ?? body?.errors ?? null;

  if (Array.isArray(rawDetail)) {
    for (const entry of rawDetail) {
      const field = locToField(entry?.loc) || entry?.field || entry?.name;
      if (!field) continue;
      if (result.fieldErrors[field]) continue;
      if (overrides[field]) {
        result.fieldErrors[field] = overrides[field];
      } else {
        result.fieldErrors[field] = translatePydanticError(field, entry, fieldLabels);
      }
    }
  } else if (rawDetail && typeof rawDetail === 'object') {
    for (const [field, m] of Object.entries(rawDetail)) {
      if (overrides[field]) {
        result.fieldErrors[field] = overrides[field];
      } else if (typeof m === 'string') {
        result.fieldErrors[field] = m;
      } else if (m && typeof m === 'object' && typeof m.msg === 'string') {
        result.fieldErrors[field] = translatePydanticError(field, m, fieldLabels);
      }
    }
  }

  result.hasFieldErrors = Object.keys(result.fieldErrors).length > 0;

  if (result.hasFieldErrors) {
    result.formMessage = Object.values(result.fieldErrors)[0];
  } else {
    result.formMessage = body?.message
      || err?.message
      || DEFAULT_FORM_ERROR_MESSAGE;
  }
  return result;
}

/**
 * Deriva `fieldLabels` automáticamente a partir del array de campos de
 * un formulario (CrudTable, AdminCrudShell, etc.). Usa `label` tal cual
 * y, si no existe, el propio `name`.
 */
export function fieldLabelsFromFormFields(formFields = []) {
  const out = {};
  for (const f of formFields || []) {
    if (!f?.name) continue;
    out[f.name] = f.label || f.name;
  }
  return out;
}
